import { Breakpoints, intl, useBreakpoint } from '../../lib';
import { useState } from 'react';
import type { Ref, ReactNode, ReactPortal } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';
import { HBox } from '../layout';
import { Tool } from './Tool';
import type { LanguageProperty } from '../generated/LanguageProperty';
import { mergeRefs, DEFAULT_PLACEMENT_CONFIG } from '../../util';
import { Arrow, useLayer } from 'react-laag';
import type { LayerProps, UseLayerArrowProps } from 'react-laag';
import { FocusTrap } from 'focus-trap-react';

/**
 * This might cause some memory to be allocated longer than it ideally should be.
 *
 * As soon as we update beyond typescript 4.3.* we can replace it with this.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakRef
 */
let closeModalPrev: null | (() => void) = null;

export type InfoModalProps = {
  settings: {
    modalTitle?: LanguageProperty;
    modalContent?: LanguageProperty | React.JSX.Element;
    modalList?: LanguageProperty[];
    orderedList: boolean;
    heading: LanguageProperty;
    removeArrow?: boolean;
    ariaLabel: LanguageProperty;
    tooltipText?: LanguageProperty;
    showCloseButton?: boolean;
  };
};

export function InfoModal({
  settings,
  ...props
}: Readonly<InfoModalProps>): React.JSX.Element {
  const mobile = useBreakpoint(Breakpoints.MOBILE);
  const dialog = useRef<HTMLDialogElement>(null);
  const getList = useMemo((): React.JSX.Element => {
    if (settings.modalList && settings.modalList.length > 0) {
      const getItems = () => {
        return (settings.modalList ?? []).map(item => (
          <li key={item}>{intl(item)}</li>
        ));
      };
      if (settings.orderedList) {
        return <ol>{getItems()}</ol>;
      } else {
        return <ul>{getItems()}</ul>;
      }
    } else {
      return <></>;
    }
  }, [settings.modalList, settings.orderedList]);

  const modalTitle = useMemo(() => {
    return settings.modalTitle ? (
      <h4 className="ehs-modalTitleH4">{intl(settings.modalTitle)}</h4>
    ) : (
      <div className="ehs-modalTitleEmpty" />
    );
  }, [settings.modalTitle]);

  const [isOpen, setOpen] = useState(false);
  const { layerProps, triggerProps, renderLayer, arrowProps } = useLayer({
    isOpen,
    onOutsideClick: () => setOpen(prev => !prev),
    ...DEFAULT_PLACEMENT_CONFIG
  });

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const open = useCallback(() => {
    if (mobile) {
      dialog.current?.showModal();
      return;
    }
    if (closeModalPrev != null) {
      closeModalPrev();
    }
    closeModalPrev = () => setOpen(false);
    setOpen(true);
  }, [mobile, setOpen]);

  const element = UseCreateToggleLayer({
    renderLayer,
    arrowProps,
    layerProps,
    modalTitle: modalTitle,
    modalContent: settings.modalContent,
    list: getList,
    removeArrow: settings.removeArrow,
    showCloseButton: settings.showCloseButton,
    setClose: () => setOpen(false)
  });

  return (
    <HBox {...props}>
      {isOpen &&
        (!mobile ? (
          element
        ) : (
          <InfoModalDialogComponent
            modalTitle={modalTitle}
            modalContent={settings.modalContent}
            list={getList}
            removeArrow={settings.removeArrow}
            ref={dialog}
            layerProps={layerProps}
            arrowProps={arrowProps}
            renderLayer={renderLayer}
            setClose={() => setOpen(false)}
          />
        ))}
      {settings.heading && (
        <h3 className="ehs-infoModalHeading">{intl(settings.heading)}</h3>
      )}
      <div className="ehs-infoModalIconWrapper" {...triggerProps}>
        <Tool
          onClick={isOpen ? close : open}
          icon="shui_information"
          text=""
          aria-label={intl(settings.ariaLabel)}
          tooltip={
            settings.tooltipText ? intl(settings.tooltipText) : undefined
          }
        />
      </div>
    </HBox>
  );
}

type InfoModalComponentProps = {
  renderLayer: (children: ReactNode) => ReactPortal | null;
  layerProps: LayerProps;
  arrowProps: UseLayerArrowProps;
  modalTitle: React.JSX.Element;
  modalContent?: LanguageProperty | React.JSX.Element;
  list: React.JSX.Element;
  removeArrow?: boolean;
  showCloseButton?: boolean;
  setClose: () => void;
};
const InfoModalDialogComponent = function InfoModalDialogComponent({
  layerProps,
  modalTitle,
  modalContent,
  list,
  ref
}: InfoModalComponentProps & {
  ref?: Ref<HTMLDialogElement>;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  return (
    <dialog
      {...layerProps}
      ref={mergeRefs([ref, dialog])}
      className="ehs-infoModalDialog"
    >
      <div className="ehs-infoModalTitle">
        {modalTitle}
        <Tool
          icon={'materialicon-close'}
          onClick={() => dialog.current?.close()}
          text=""
          aria-label={intl('view.button.close')}
        />
      </div>
      <div className="ehs-modalContent">
        {modalContent && typeof modalContent == 'string' ? (
          <span>{intl(modalContent)}</span>
        ) : (
          modalContent
        )}
        {list}
      </div>
    </dialog>
  );
};

function UseCreateToggleLayer({
  renderLayer,
  layerProps,
  arrowProps,
  modalTitle,
  modalContent,
  list,
  removeArrow,
  setClose,
  showCloseButton = true
}: Readonly<InfoModalComponentProps>) {
  return renderLayer(
    <FocusTrap
      active={showCloseButton}
      focusTrapOptions={{
        initialFocus: '.ehs-infoModalWrapper ',
        escapeDeactivates: false,
        allowOutsideClick: true
      }}
    >
      <div
        className="ehs-infoModalWrapper"
        {...layerProps}
        style={{ ...layerProps.style }}
      >
        <div className="ehs-infoModal" aria-live="polite">
          <HBox className="ehs-infoModalTitle">
            <div className="header-container">
              <span>{modalTitle}</span>
              {showCloseButton && (
                <Tool
                  icon={'materialicon-close'}
                  onClick={setClose}
                  text=""
                  aria-label={intl('view.button.close')}
                />
              )}
            </div>
          </HBox>
          <div className="ehs-modalContent">
            {modalContent && typeof modalContent == 'string' ? (
              <span>{intl(modalContent)}</span>
            ) : (
              modalContent
            )}
            {list}
          </div>
        </div>
        {!removeArrow && (
          <Arrow
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
            {...arrowProps}
            size={10}
            roundness={0}
            borderWidth={1}
            backgroundColor="var(--base-0)"
            borderColor="var(--base-1)"
          />
        )}
      </div>
    </FocusTrap>
  );
}
